import { ref, onMounted, onUnmounted } from 'vue'
import { Capacitor } from '@capacitor/core'
import { AdManager, type AdManagerConfig, type NativeAdData } from '~/utils/ad-manager'

export function useAdManager() {
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const currentAd = ref<NativeAdData | null>(null)
  const error = ref<string | null>(null)
  const isMobile = ref(false)

  // Check if we're on a mobile platform
  onMounted(() => {
    if (import.meta.client) {
      const platform = Capacitor.getPlatform()
      isMobile.value = platform === 'ios' || platform === 'android'
    }
  })

  /**
   * Initialize Ad Manager
   */
  async function initialize(config: AdManagerConfig) {
    if (!isMobile.value) {
      console.warn('AdManager is only available on mobile platforms')
      return
    }

    try {
      await AdManager.initialize(config)
      isInitialized.value = true
      error.value = null
    }
    catch (err: any) {
      error.value = err.message || 'Failed to initialize Ad Manager'
      console.error('AdManager initialization error:', err)
      throw err
    }
  }

  /**
   * Load a native ad
   */
  async function loadAd(adUnitId?: string): Promise<NativeAdData | null> {
    if (!isMobile.value) {
      console.warn('AdManager is only available on mobile platforms')
      return null
    }

    if (!isInitialized.value) {
      throw new Error('AdManager not initialized. Call initialize() first.')
    }

    isLoading.value = true
    error.value = null

    try {
      const adData = await AdManager.loadNativeAd({ adUnitId })
      currentAd.value = adData
      isLoading.value = false
      return adData
    }
    catch (err: any) {
      error.value = err.message || 'Failed to load ad'
      isLoading.value = false
      console.error('AdManager load error:', err)
      throw err
    }
  }

  /**
   * Track ad click
   */
  async function trackClick(adUnitId: string) {
    if (!isMobile.value) {
      return
    }

    try {
      await AdManager.trackClick({ adUnitId })
    }
    catch (err: any) {
      console.error('AdManager trackClick error:', err)
    }
  }

  /**
   * Track ad impression
   */
  async function trackImpression(adUnitId: string) {
    if (!isMobile.value) {
      return
    }

    try {
      await AdManager.trackImpression({ adUnitId })
    }
    catch (err: any) {
      console.error('AdManager trackImpression error:', err)
    }
  }

  /**
   * Clear current ad
   */
  function clearAd() {
    currentAd.value = null
    error.value = null
  }

  // Set up event listeners
  let adLoadedListener: any = null
  let adFailedListener: any = null
  let adClickedListener: any = null
  let adImpressionListener: any = null

  onMounted(() => {
    if (isMobile.value) {
      // Listen for ad loaded events
      AdManager.addListener('adLoaded', (data: NativeAdData) => {
        currentAd.value = data
        isLoading.value = false
      })

      // Listen for ad failed events
      AdManager.addListener('adFailedToLoad', (data: any) => {
        error.value = data.error || 'Failed to load ad'
        isLoading.value = false
      })

      // Listen for ad clicked events
      AdManager.addListener('adClicked', (data: any) => {
        console.log('Ad clicked:', data.adUnitId)
      })

      // Listen for ad impression events
      AdManager.addListener('adImpression', (data: any) => {
        console.log('Ad impression:', data.adUnitId)
      })
    }
  })

  onUnmounted(() => {
    if (isMobile.value) {
      AdManager.removeAllListeners()
    }
  })

  return {
    isInitialized,
    isLoading,
    currentAd,
    error,
    isMobile,
    initialize,
    loadAd,
    trackClick,
    trackImpression,
    clearAd,
  }
}

